import { DataTable } from "./DataTable/DataTable";
import { Filters, Patient } from "../types";
import { Plus, Edit3, Trash2, Eye, Search } from "lucide-react";
import { useState, useEffect } from "react";
import avatarPlaceholder from "../assets/images/default_avatar.jpg";
import { PatientFilters } from "./Filters/PatientFilters";
import { AddNewPatientModal, formatAge } from "../shared/AddNewPatientModal";
import toast from "react-hot-toast";
import { PatientService } from "../services/api/patient.service";
import { NewPatientData } from "./Appointments/types";
import PatientsDetails from "./patients/PatientsDetails";
import { useAuth } from "../context/AuthContext";

export function PatientsView() {
  const [showPatientDetails, setShowPatientDetails] = useState(false);
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [filteredData, setFilterdData] = useState([]);
  const [patientData, setPatientData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const { user } = useAuth();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItemsCount, setTotalItemsCount] = useState(0);
  const [nextPage, setNextPage] = useState<number | null>(null);
  const [previousPage, setPreviousPage] = useState<number | null>(null);
  const [filterDetails, setFilterDetails] = useState<Filters>({
    success: false,
    gender: "",
    status: "",
  });

  const columns = [
    {
      key: "name",
      title: "Name",
      sortable: true,
      render: (patient: Patient) => {
        const profileImageSrc =
          patient.profileImage instanceof File
            ? URL.createObjectURL(patient.profileImage)
            : patient.profileImage || avatarPlaceholder;

        return (
          <div className="flex items-center">
            <img
              src={profileImageSrc}
              alt={patient.name}
              className="w-8 h-8 rounded-full mr-3"
            />
            <span>{patient.name}</span>
          </div>
        );
      },
    },
    {
      key: "mobileNumber",
      title: "Mobile Number",
      sortable: true,
    },
    {
      key: "age",
      title: "Age",
      sortable: true,
      render: (patient: Patient) => <span>{patient.age}</span>,
    },
    {
      key: "gender",
      title: "Gender",
      sortable: true,
      filterable: true,
    },
    {
      key: "registeredDate",
      title: "Registered Date",
      sortable: true,
    },
    {
      key: "actions",
      title: "Actions",
      width: "w-[200px]",
      render: (patient: Patient) => (
        <div className="flex items-center gap-4">
          {/* View Details */}
          <button
            className="text-blue-600 hover:text-blue-700"
            onClick={() => handleViewPatientDetails(patient)}
            aria-label={`View details of ${patient.name}`}
            title={`View details of ${patient.name}`}
          >
            <Eye className="w-5 h-5" />
          </button>

          {user?.role?.toLocaleLowerCase() !== "admin" && (
            <>
              {/* EDIT */}
              {/* <button
                className="text-green-600 hover:text-green-700"
                onClick={() => handleEditClick(patient)}
                title={`Edit Patient details of ${patient.name}`}
              >
                <Edit3 className="w-5 h-5" />
              </button> */}

              {/* Delete Button */}
              {/* <button
                className={`text-red-600 hover:text-red-700 ${
                  isDeleting ? "opacity-50 cursor-not-allowed" : ""
                }`}
                onClick={() => handleDeleteClick(patient.userId)}
                disabled={isDeleting}
                title={`Delete Patient details of ${patient.name}`}
              >
                <Trash2 className="w-5 h-5" />
              </button> */}
            </>
          )}
        </div>
      ),
    },
  ];

  const handleViewPatientDetails = (patient: Patient) => {
    setShowPatientDetails(true);
    setSelectedPatient(patient);
  };

  const addPatientButton = (
    <button
      onClick={() => setShowAddPatient(true)}
      className="relative z-10 flex items-center justify-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors whitespace-nowrap"
    >
      <Plus className="w-5 h-5" />
      Add Patient
    </button>
  );

  const getPatientData = async (
    page: number,
    pageSize: number,
    gender: string | null,
    status: string | null,
    searchText: ""
  ) => {
    try {
      setIsLoading(true);
      console.log(searchText)
      const response = await PatientService.getPatients({
        page,
        pageSize,
        gender,
        status,
        clinicId: user?.role === "clinic" ? user?.id : "",
        medicalCampId: user?.role === "medicalcamp" ? user?.id : "",
        searchText,
      });
      if (response.success && response.data?.dataList) {
        const updatedPatientData = response?.data.dataList?.map(
          (data: Patient) => ({
            ...data,
            registeredDate: data.registeredDate?.split("T")[0],
          })
        );
        setPatientData(updatedPatientData);
        setFilterdData(updatedPatientData);
        setCurrentPage(response.data.currentPage);
        setTotalPages(response.data.totalPages);
        setNextPage(response.data.nextPage);
        setPreviousPage(response.data.previousPage);
      }
    } catch (error) {
      console.log(error);
      toast.error("Failed to load");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchText);
      setCurrentPage(1); // reset to page 1 on new search
    }, 300);

    return () => clearTimeout(handler);
  }, [searchText]);

  useEffect(() => {
    getPatientData(
      currentPage,
      pageSize,
      filterDetails.gender,
      filterDetails.status,
      debouncedSearch ?? ""
    );
  }, [
    currentPage,
    pageSize,
    filterDetails.gender,
    filterDetails.status,
    debouncedSearch,
  ]);

  const handleDeleteClick = async (patientId: string) => {
    try {
      const response = await PatientService.deletePatient(patientId);

      if (response.success) {
        toast.success("Patient deleted successfully!");
        getPatientData(currentPage, pageSize, "", "", "");
      } else {
        toast.error(response.message || "Failed to delete patient.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    }
  };

  const handleAddPatientSuccess = (patientData: NewPatientData) => {
    console.log("New patient added:", patientData);
    setShowAddPatient(false);
    getPatientData(currentPage, pageSize, "", "", "");
    getPatientData(currentPage, pageSize, "", "", "");
  };

  const handleEditClick = (patient: Patient) => {
    setSelectedPatient(patient);
    setShowAddPatient(true);
  };

  return (
    <div className="relative">
      {!showPatientDetails && (
        <div
          className={`flex justify-end ${
            user?.role.toLocaleLowerCase() === "admin"
              ? "relative"
              : user?.role.toLocaleLowerCase() === "clinic"
              ? "relative right-12 top-9"
              : user?.role.toLocaleLowerCase() === "medicalcamp"
              ? "relative right-12 top-9"
              : null
          }`}
        >
          <div>
            <Search className="relative top-[2.25rem] left-4 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search patients..."
              value={searchText}
              onChange={handleSearchChange}
              className="border rounded-lg px-3 py-2.5 text-sm w-64 text-center"
            />
          </div>
        </div>
      )}
      {!showPatientDetails && (
        <DataTable
          data={filteredData}
          columns={columns}
          searchable
          searchFields={[
            "name",
            "mobileNumber",
            "gender",
            "age",
            "registeredDate",
          ]}
          itemsPerPage={pageSize}
          isLoading={isLoading}
          onRowClick={(patient) => console.log("clicked:", patient)}
          customFilters={
            user?.role?.toLocaleLowerCase() !== "admin"
              ? addPatientButton
              : null
          }
          filterComponent={
            <PatientFilters
              filterDetails={filterDetails}
              setFilterDetails={setFilterDetails}
            />
          }
          pagination={{
            currentPage,
            setCurrentPage,
            setTotalPages,
            totalPages,
            pageSize,
            setPageSize,
          }}
        />
      )}
      {showAddPatient && (
        <AddNewPatientModal
          mobileNumber=""
          onClose={() => {
            setShowAddPatient(false);
            setSelectedPatient(null);
          }}
          onSuccess={handleAddPatientSuccess}
          initialData={selectedPatient}
          isVerifiedProps={selectedPatient?.userId ? true : false}
        />
      )}

      {showPatientDetails && (
        <PatientsDetails
          showHistory={true}
          selectedPatient={selectedPatient}
          onClose={() => {
            setShowPatientDetails(false);
            setSelectedPatient(null);
          }}
        />
      )}
    </div>
  );
}
